import {
  Alert,
  DocumentListItemSkeleton,
  DocumentList as DocumentRows,
  EmptyState,
  ListPanel,
} from '@all-manager/design-system-v2';
import type { DocumentFilters } from '../../api/keys';
import NoDocumentsIcon from '../../assets/no-documents.svg?react';
import DocumentRow from '../DocumentRow/DocumentRow';
import DocumentsPagination from '../DocumentsPagination/DocumentsPagination';
import useDocumentList from './useDocumentList';

type Props = {
  filters: DocumentFilters;
  emptyTitle?: string;
  emptySubtitle?: string;
  /** Hide each document's provider, e.g. inside the provider's own page. */
  hideProvider?: boolean;
};

const SKELETON_ROWS = [0, 1, 2];

export default function DocumentList(props: Props) {
  const hook = useDocumentList(props);

  if (hook.isEmpty) {
    return (
      <EmptyState
        title={props.emptyTitle ?? 'Aucun document'}
        subtitle={props.emptySubtitle ?? 'Retrouvez ici tous les documents associés.'}
        image={<NoDocumentsIcon />}
      />
    );
  }

  if (hook.isError) {
    return <Alert severity="error">Les documents n’ont pas pu être chargés. Rechargez la page pour réessayer.</Alert>;
  }

  return (
    <ListPanel
      footer={hook.showPagination ? (
        <DocumentsPagination
          count={hook.count}
          page={hook.page}
          rowsPerPage={hook.rowsPerPage}
          onPageChange={hook.handlePageChange}
          onRowsPerPageChange={hook.handleRowsPerPageChange}
        />
      ) : undefined}
    >
      {hook.isLoading ? (
        <DocumentRows aria-label="Documents" aria-busy>
          {SKELETON_ROWS.map((row) => <DocumentListItemSkeleton key={row} />)}
        </DocumentRows>
      ) : (
        <DocumentRows aria-label="Documents">
          {hook.documents.map((doc) => (
            <DocumentRow key={doc.id} document={doc} hideProvider={props.hideProvider} />
          ))}
        </DocumentRows>
      )}
    </ListPanel>
  );
}
